import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { Accordion, Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { deleteData, fetchData, postData, putData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import './AdminContentEditors.css';

const EMPTY_FORM = {
  sectionName: '',
  value: '',
  displayText: '',
  sortOrder: 0,
  isActive: true,
};

const isActiveValue = (value) => (
  value === true ||
  value === 1 ||
  String(value ?? '').trim().toLowerCase() === 'true'
);

const groupBubbles = (items) => {
  const grouped = {};
  items.forEach((item) => {
    const sectionName = String(item.SectionName || 'Unknown').trim() || 'Unknown';
    const value = String(item.Value || 'Unknown').trim() || 'Unknown';
    grouped[sectionName] = grouped[sectionName] || {};
    grouped[sectionName][value] = grouped[sectionName][value] || [];
    grouped[sectionName][value].push(item);
  });

  Object.values(grouped).forEach((locations) => {
    Object.values(locations).forEach((bubbles) => {
      bubbles.sort((left, right) => (
        (Number(left.SortOrder) || 0) - (Number(right.SortOrder) || 0)
      ));
    });
  });
  return grouped;
};

const AdminInfoBubbles = () => {
  const apiUrls = useSelector(selectApiUrls);
  const dispatch = useDispatch();
  const [bubbles, setBubbles] = useState([]);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingBubble, setEditingBubble] = useState(null);
  const [editingSortOrder, setEditingSortOrder] = useState(null);
  const [tempSortOrder, setTempSortOrder] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingAction, setPendingAction] = useState('');
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  useGuardedRealtimeList({ apiKey: 'iconData', apply: setBubbles,
    blocked: loading || refreshing || Boolean(pendingAction || editingSortOrder !== null || showModal || deleteTarget) });

  const fetchInfoBubbles = useCallback(async ({ background = false } = {}) => {
    if (background) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const data = await dispatch(fetchData({ url: apiUrls.iconData })).unwrap();
      setBubbles(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(`Failed to fetch information bubbles: ${err.message || err}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dispatch, apiUrls.iconData]);

  useEffect(() => {
    fetchInfoBubbles();
  }, [fetchInfoBubbles]);

  const filteredBubbles = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return bubbles.filter((bubble) => {
      const active = isActiveValue(bubble.IsActive);
      if (statusFilter === 'active' && !active) return false;
      if (statusFilter === 'inactive' && active) return false;
      if (!normalizedSearch) return true;
      return [bubble.SectionName, bubble.Value, bubble.DisplayText]
        .some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
    });
  }, [bubbles, searchTerm, statusFilter]);

  const groupedData = useMemo(() => groupBubbles(filteredBubbles), [filteredBubbles]);
  const allGroupedData = useMemo(() => groupBubbles(bubbles), [bubbles]);
  const sectionNames = useMemo(
    () => Object.keys(groupedData).sort((left, right) => left.localeCompare(right)),
    [groupedData]
  );
  const summary = useMemo(() => ({
    bubbles: bubbles.length,
    sections: Object.keys(allGroupedData).length,
    locations: Object.values(allGroupedData).reduce(
      (total, locations) => total + Object.keys(locations).length,
      0
    ),
    active: bubbles.filter((bubble) => isActiveValue(bubble.IsActive)).length,
  }), [allGroupedData, bubbles]);

  const getNextSortOrder = (sectionName, value) => {
    const items = allGroupedData[sectionName]?.[value] || [];
    if (items.length === 0) return 0;
    return Math.max(...items.map((item) => Number(item.SortOrder) || 0)) + 1;
  };

  const handleAddNew = (prefillSection = '', prefillValue = '') => {
    setEditingBubble(null);
    setFormData({
      ...EMPTY_FORM,
      sectionName: prefillSection,
      value: prefillValue,
      sortOrder: getNextSortOrder(prefillSection, prefillValue),
    });
    setModalMode('add');
    setModalError('');
    setShowModal(true);
  };

  const handleEdit = (bubble) => {
    setEditingBubble(bubble);
    setFormData({
      sectionName: bubble.SectionName || '',
      value: bubble.Value || '',
      displayText: bubble.DisplayText || '',
      sortOrder: Number(bubble.SortOrder) || 0,
      isActive: isActiveValue(bubble.IsActive),
    });
    setModalMode('edit');
    setModalError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (pendingAction) return;
    setShowModal(false);
    setEditingBubble(null);
    setFormData(EMPTY_FORM);
    setModalError('');
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!formData.sectionName.trim() || !formData.value.trim() || !formData.displayText.trim()) {
      setModalError('Section, location, and display text are required.');
      return;
    }

    const isEditing = modalMode === 'edit' && editingBubble;
    setPendingAction(isEditing ? `edit:${editingBubble.ID}` : 'create');
    setModalError('');
    setSuccessMessage('');

    const normalizedData = {
      sectionName: formData.sectionName.trim(),
      value: formData.value.trim(),
      displayText: formData.displayText.trim(),
      sortOrder: Math.max(0, Number.parseInt(formData.sortOrder, 10) || 0),
      isActive: Boolean(formData.isActive),
    };

    try {
      if (isEditing) {
        await dispatch(putData({
          url: `${apiUrls.iconData}?id=${editingBubble.ID}`,
          data: {
            SectionName: normalizedData.sectionName,
            Value: normalizedData.value,
            DisplayText: normalizedData.displayText,
            SortOrder: normalizedData.sortOrder,
            IsActive: normalizedData.isActive,
          },
        })).unwrap();
      } else {
        await dispatch(postData({ url: apiUrls.iconData, data: normalizedData })).unwrap();
      }

      setShowModal(false);
      setEditingBubble(null);
      setFormData(EMPTY_FORM);
      await fetchInfoBubbles({ background: true });
      setSuccessMessage(isEditing ? 'Information bubble updated.' : 'Information bubble created.');
    } catch (err) {
      setModalError(`Failed to save information bubble: ${err.message || err}`);
    } finally {
      setPendingAction('');
    }
  };

  const handleStartEditingSortOrder = (bubble) => {
    setEditingSortOrder(bubble.ID);
    setTempSortOrder(String(bubble.SortOrder ?? 0));
  };

  const cancelSortOrderEdit = () => {
    setEditingSortOrder(null);
    setTempSortOrder('');
  };

  const saveSortOrder = async (bubble) => {
    const newSortOrder = Number.parseInt(tempSortOrder, 10);
    if (!Number.isInteger(newSortOrder) || newSortOrder < 0) {
      setError('Sort order must be a non-negative whole number.');
      cancelSortOrderEdit();
      return;
    }
    if (newSortOrder === Number(bubble.SortOrder)) {
      cancelSortOrderEdit();
      return;
    }

    setPendingAction(`sort:${bubble.ID}`);
    setError(null);
    try {
      await dispatch(putData({
        url: `${apiUrls.iconData}?id=${bubble.ID}`,
        data: {
          SectionName: bubble.SectionName,
          Value: bubble.Value,
          DisplayText: bubble.DisplayText,
          SortOrder: newSortOrder,
          IsActive: isActiveValue(bubble.IsActive),
        },
      })).unwrap();
      cancelSortOrderEdit();
      await fetchInfoBubbles({ background: true });
      setSuccessMessage('Sort order updated.');
    } catch (err) {
      setError(`Failed to update sort order: ${err.message || err}`);
      cancelSortOrderEdit();
    } finally {
      setPendingAction('');
    }
  };

  const handleSortOrderKeyDown = (event, bubble) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveSortOrder(bubble);
    } else if (event.key === 'Escape') {
      cancelSortOrderEdit();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setPendingAction(`delete:${deleteTarget.ID}`);
    setError(null);
    setSuccessMessage('');
    try {
      await dispatch(deleteData({ url: `${apiUrls.iconData}?id=${deleteTarget.ID}` })).unwrap();
      setDeleteTarget(null);
      await fetchInfoBubbles({ background: true });
      setSuccessMessage('Information bubble deleted.');
    } catch (err) {
      setError(`Failed to delete information bubble: ${err.message || err}`);
    } finally {
      setPendingAction('');
    }
  };

  const updateForm = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  if (loading) {
    return (
      <div className="admin-editor__loading" role="status" aria-live="polite">
        <Spinner animation="border" variant="primary" />
        <div><strong>Loading information bubbles</strong><span>Preparing the tooltip editor...</span></div>
      </div>
    );
  }

  return (
    <section className="admin-editor" aria-labelledby="info-bubbles-title">
      <header className="admin-editor__header">
        <div>
          <p className="admin-editor__eyebrow">Fields &amp; content</p>
          <h2 id="info-bubbles-title">Information bubbles</h2>
          <p>Manage contextual help organized by portal section and interface location.</p>
        </div>
        <div className="admin-editor__header-actions">
          <Button
            variant="outline-secondary"
            onClick={() => fetchInfoBubbles({ background: true })}
            disabled={refreshing || Boolean(pendingAction)}
          >
            <i className={`bi bi-arrow-clockwise me-2 ${refreshing ? 'admin-editor__spin' : ''}`} />
            {refreshing ? 'Refreshing' : 'Refresh'}
          </Button>
          <Button onClick={() => handleAddNew()} disabled={Boolean(pendingAction)}>
            <i className="bi bi-plus-lg me-2" />New bubble
          </Button>
        </div>
      </header>

      <div className="admin-editor__metrics" aria-label="Information bubble summary">
        <div><span>Information bubbles</span><strong>{summary.bubbles}</strong></div>
        <div><span>Sections</span><strong>{summary.sections}</strong></div>
        <div><span>Locations</span><strong>{summary.locations}</strong></div>
        <div className="admin-editor__metric--success"><span>Active</span><strong>{summary.active}</strong></div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="admin-editor__alert">
          <Alert.Heading>We couldn&apos;t complete that action</Alert.Heading>
          <p className="mb-0">{error}</p>
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage('')} className="admin-editor__alert" aria-live="polite">
          <i className="bi bi-check-circle-fill me-2" />{successMessage}
        </Alert>
      )}

      <div className="admin-editor__workspace">
        <div className="admin-editor__toolbar">
          <div className="admin-editor__search">
            <i className="bi bi-search" aria-hidden="true" />
            <Form.Control
              type="search"
              placeholder="Search sections, locations, or bubble text"
              aria-label="Search information bubbles"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} aria-label="Clear search">
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>
          <Form.Group className="admin-editor__filter">
            <Form.Label htmlFor="info-bubble-status-filter">Status</Form.Label>
            <Form.Select
              id="info-bubble-status-filter"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All bubbles</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </Form.Select>
          </Form.Group>
          {(searchTerm || statusFilter !== 'all') && (
            <Button variant="link" onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}>Reset filters</Button>
          )}
          <span className="admin-editor__result-count">Showing {filteredBubbles.length} of {bubbles.length}</span>
        </div>

        {sectionNames.length > 0 ? (
          <Accordion
            activeKey={openAccordion}
            onSelect={(eventKey) => setOpenAccordion(eventKey)}
            className="admin-editor__accordion"
          >
            {sectionNames.map((sectionName) => {
              const locations = groupedData[sectionName];
              const sectionBubbleCount = Object.values(locations).reduce((total, items) => total + items.length, 0);
              return (
                <Accordion.Item eventKey={sectionName} key={sectionName}>
                  <Accordion.Header>
                    <span className="admin-editor__accordion-title">
                      <span>{sectionName}</span>
                      <span>
                        {Object.keys(locations).length} location{Object.keys(locations).length === 1 ? '' : 's'}
                        <Badge bg="light" text="dark" pill>{sectionBubbleCount} bubbles</Badge>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="admin-editor__section-action">
                      <Button variant="outline-primary" size="sm" onClick={() => handleAddNew(sectionName, '')}>
                        <i className="bi bi-plus-circle me-2" />Add location to {sectionName}
                      </Button>
                    </div>

                    {Object.keys(locations).sort((left, right) => left.localeCompare(right)).map((value) => (
                      <article className="admin-editor__location" key={value}>
                        <header>
                          <div>
                            <span className="admin-editor__location-label">Location</span>
                            <h3><i className="bi bi-geo-alt me-2" />{value}</h3>
                          </div>
                          <Button variant="outline-primary" size="sm" onClick={() => handleAddNew(sectionName, value)}>
                            <i className="bi bi-plus-lg me-1" />Add bubble
                          </Button>
                        </header>
                        <Table responsive hover className="admin-editor__table admin-editor__table--nested align-middle">
                          <thead>
                            <tr>
                              <th>Display text</th>
                              <th>Order</th>
                              <th>Status</th>
                              <th><span className="visually-hidden">Actions</span></th>
                            </tr>
                          </thead>
                          <tbody>
                            {locations[value].map((bubble) => (
                              <tr key={bubble.ID} className={isActiveValue(bubble.IsActive) ? '' : 'admin-editor__inactive-row'}>
                                <td className="admin-editor__content-cell">
                                  <span className="admin-editor__bubble-preview" title={bubble.DisplayText}>{bubble.DisplayText}</span>
                                </td>
                                <td>
                                  {editingSortOrder === bubble.ID ? (
                                    <div className="admin-editor__inline-order">
                                      <Form.Control
                                        type="number"
                                        min="0"
                                        size="sm"
                                        value={tempSortOrder}
                                        onChange={(event) => setTempSortOrder(event.target.value)}
                                        onKeyDown={(event) => handleSortOrderKeyDown(event, bubble)}
                                        aria-label={`Sort order for ${bubble.DisplayText}`}
                                        autoFocus
                                      />
                                      <Button size="sm" onClick={() => saveSortOrder(bubble)} disabled={Boolean(pendingAction)}>
                                        {pendingAction === `sort:${bubble.ID}` ? <Spinner size="sm" /> : <i className="bi bi-check-lg" />}
                                      </Button>
                                      <Button size="sm" variant="outline-secondary" onClick={cancelSortOrderEdit} disabled={Boolean(pendingAction)}>
                                        <i className="bi bi-x-lg" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="admin-editor__order-button"
                                      onClick={() => handleStartEditingSortOrder(bubble)}
                                      disabled={Boolean(pendingAction)}
                                      title="Edit sort order"
                                    >
                                      {bubble.SortOrder ?? 0}<i className="bi bi-pencil ms-2" />
                                    </Button>
                                  )}
                                </td>
                                <td>
                                  <Badge bg={isActiveValue(bubble.IsActive) ? 'success' : 'secondary'} pill>
                                    {isActiveValue(bubble.IsActive) ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                                <td className="text-end">
                                  <div className="admin-editor__row-actions">
                                    <Button size="sm" variant="outline-primary" onClick={() => handleEdit(bubble)} disabled={Boolean(pendingAction)}>
                                      <i className="bi bi-pencil me-1" />Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => setDeleteTarget(bubble)}
                                      disabled={Boolean(pendingAction)}
                                      aria-label={`Delete information bubble ${bubble.DisplayText}`}
                                    >
                                      <i className="bi bi-trash" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </article>
                    ))}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        ) : (
          <div className="admin-editor__empty">
            <i className="bi bi-search" />
            <h3>No matching information bubbles</h3>
            <p>Try another search or reset the status filter.</p>
            <Button variant="outline-primary" onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}>Reset filters</Button>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered backdrop="static">
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton={!pendingAction}>
            <div>
              <p className="admin-editor__eyebrow mb-1">{modalMode === 'edit' ? 'Update' : 'Create'}</p>
              <Modal.Title>{modalMode === 'edit' ? 'Edit information bubble' : 'New information bubble'}</Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted">Choose where this help appears and write concise, action-oriented guidance.</p>
            {modalError && <Alert variant="danger">{modalError}</Alert>}
            <div className="row">
              <div className="col-md-6">
                <Form.Group controlId="info-bubble-section" className="mb-3">
                  <Form.Label>Section</Form.Label>
                  <Form.Control
                    value={formData.sectionName}
                    onChange={(event) => updateForm('sectionName', event.target.value)}
                    placeholder="Example: Job Schedule"
                    disabled={Boolean(pendingAction)}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="info-bubble-location" className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    value={formData.value}
                    onChange={(event) => updateForm('value', event.target.value)}
                    placeholder="Example: Release button"
                    disabled={Boolean(pendingAction)}
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group controlId="info-bubble-text" className="mb-3">
              <div className="d-flex justify-content-between gap-3">
                <Form.Label>Display text</Form.Label>
                <span className="text-muted small">{formData.displayText.length} characters</span>
              </div>
              <Form.Control
                as="textarea"
                rows={7}
                value={formData.displayText}
                onChange={(event) => updateForm('displayText', event.target.value)}
                placeholder="Write the guidance shown to users."
                disabled={Boolean(pendingAction)}
              />
            </Form.Group>
            <div className="row align-items-center">
              <div className="col-md-6">
                <Form.Group controlId="info-bubble-order" className="mb-3">
                  <Form.Label>Sort order</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={formData.sortOrder}
                    onChange={(event) => updateForm('sortOrder', event.target.value)}
                    disabled={Boolean(pendingAction)}
                  />
                  <Form.Text>Lower numbers appear first at the same location.</Form.Text>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Check
                  id="info-bubble-active"
                  type="switch"
                  label="Active and visible to users"
                  checked={formData.isActive}
                  onChange={(event) => updateForm('isActive', event.target.checked)}
                  disabled={Boolean(pendingAction)}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseModal} disabled={Boolean(pendingAction)}>Cancel</Button>
            <Button
              type="submit"
              disabled={Boolean(pendingAction) || !formData.sectionName.trim() || !formData.value.trim() || !formData.displayText.trim()}
            >
              {pendingAction && !pendingAction.startsWith('delete:') && <Spinner size="sm" className="me-2" />}
              {modalMode === 'edit' ? 'Save changes' : 'Create bubble'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(deleteTarget)} onHide={() => !pendingAction && setDeleteTarget(null)} centered backdrop="static">
        <Modal.Header closeButton={!pendingAction}><Modal.Title>Delete information bubble?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>This guidance will be permanently removed from <strong>{deleteTarget?.SectionName}</strong> / <strong>{deleteTarget?.Value}</strong>.</p>
          <Alert variant="warning" className="mb-0">Consider marking it inactive if it may be needed again.</Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleteTarget(null)} disabled={Boolean(pendingAction)}>Keep bubble</Button>
          <Button variant="danger" onClick={handleDelete} disabled={Boolean(pendingAction)}>
            {pendingAction.startsWith('delete:') && <Spinner size="sm" className="me-2" />}Delete bubble
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default AdminInfoBubbles;
